import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 25  -  Monte Carlo & GEANT4
// ============================================

/** 25.1 – The Monte Carlo Method: Random Sampling in Physics */
export const MonteCarloMethod = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Monte Carlo Method  -  Random Sampling", W / 2, 24);

    // Pi estimation example
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(20, 45, 220, 200, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 45, 220, 200, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Example: Estimating π", 130, 65);
    // Draw square with inscribed circle
    const sqX = 50, sqY = 80, sqS = 160;
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.strokeRect(sqX, sqY, sqS, sqS);
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(sqX + sqS / 2, sqY + sqS / 2, sqS / 2, 0, Math.PI * 2); ctx.stroke();
    // Random dots
    const seed = 42;
    let rng = seed;
    const nextRand = () => { rng = (rng * 1103515245 + 12345) & 0x7fffffff; return rng / 0x7fffffff; };
    let inside = 0, total = 80;
    for (let i = 0; i < total; i++) {
      const rx = nextRand(), ry = nextRand();
      const dx = rx - 0.5, dy = ry - 0.5;
      const isInside = dx * dx + dy * dy <= 0.25;
      if (isInside) inside++;
      ctx.fillStyle = isInside ? "#3B82F6" : "#EF4444";
      ctx.beginPath();
      ctx.arc(sqX + rx * sqS, sqY + ry * sqS, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Right side: concept
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Core Idea", 400, 55);
    const concepts = [
      "Generate random samples",
      "from probability distributions",
      "",
      "Estimate integrals, simulate",
      "complex physical processes",
      "",
      "Accuracy ∝ 1/√N",
      "(more samples → better)",
      "",
      "π ≈ 4 �- (inside/total)",
      `≈ 4 �- ${inside}/${total} ≈ ${(4 * inside / total).toFixed(2)}`,
    ];
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    concepts.forEach((co, i) => ctx.fillText(co, 400, 75 + i * 17));

    // Applications in physics
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Applications in Particle Physics", W / 2, 270);
    const apps = [
      { app: "Event generation", desc: "Simulate pp collisions (Pythia, Herwig)", color: "#3B82F6" },
      { app: "Detector response", desc: "Track particles through matter (GEANT4)", color: "#8B5CF6" },
      { app: "Phase space integration", desc: "Multi-body decay kinematics", color: "#10B981" },
      { app: "Statistical analysis", desc: "Toy MC for systematic uncertainties", color: "#F59E0B" },
    ];
    apps.forEach((a, i) => {
      ctx.fillStyle = a.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(a.app, 50, 290 + i * 18);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(a.desc, 220, 290 + i * 18);
    });
    ctx.textAlign = "center";
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.2 – Pseudo-Random Number Generators */
export const PRNGenerators = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Pseudo-Random Number Generators (PRNGs)", W / 2, 24);

    // LCG formula
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(40, 45, 480, 50, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(40, 45, 480, 50, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("Linear Congruential: x_{n+1} = (a · x_n + c) mod m", W / 2, 68);
    ctx.fillStyle = "#3B82F6"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Deterministic: same seed → same sequence (reproducibility!)", W / 2, 88);

    // Generator comparison table
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Common Generators in HEP", W / 2, 120);
    const gens = [
      { name: "LCG (simple)", period: "~2³¹", quality: "Fast but short period", use: "Teaching" },
      { name: "Mersenne Twister", period: "2^19937 − 1", quality: "Good uniformity, standard", use: "ROOT, Python" },
      { name: "RANLUX", period: "~10¹⁷¹", quality: "Luxury levels 1–4, provable", use: "GEANT4 default" },
      { name: "Philox/Threefry", period: "2¹²⁸", quality: "Counter-based, parallel-safe", use: "GPU MC" },
    ];
    const headers = ["Generator", "Period", "Properties", "Used in"];
    ctx.font = "bold 11px Inter,sans-serif"; ctx.fillStyle = "#64748B";
    headers.forEach((h, i) => ctx.fillText(h, 70 + i * 135, 140));
    ctx.font = "11px Inter,sans-serif"; ctx.fillStyle = "#475569";
    gens.forEach((g, i) => {
      const y = 160 + i * 22;
      [g.name, g.period, g.quality, g.use].forEach((v, j) =>
        ctx.fillText(v, 70 + j * 135, y)
      );
    });

    // Quality requirements
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Requirements for Physics Simulation", W / 2, 260);
    const reqs = [
      { req: "Long period", why: "Need > 10¹² random numbers per run", color: "#3B82F6" },
      { req: "Uniformity", why: "No gaps or clusters in N-dimensional space", color: "#8B5CF6" },
      { req: "Reproducibility", why: "Same seed → identical physics results", color: "#10B981" },
      { req: "Speed", why: "~10⁹ calls per second needed for LHC MC", color: "#F59E0B" },
    ];
    reqs.forEach((r, i) => {
      ctx.fillStyle = r.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(r.req, 50, 280 + i * 20);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(r.why, 190, 280 + i * 20);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Never use rand() from C/C++ stdlib for physics  -  use validated generators!", W / 2, 355);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.3 – Particle Transport Simulation Principles */
export const ParticleTransport = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Particle Transport Simulation", W / 2, 24);

    // Step-by-step transport algorithm
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Transport Algorithm (each step)", W / 2, 50);

    const steps = [
      { label: "1. Sample\nstep length", desc: "From cross sections\nvia exponential dist.", color: "#3B82F6", bg: "#DBEAFE" },
      { label: "2. Propagate\nparticle", desc: "Move through geometry\n(straight or curved)", color: "#8B5CF6", bg: "#EDE9FE" },
      { label: "3. Check\nboundary", desc: "Enter new volume?\nApply new material", color: "#10B981", bg: "#D1FAE5" },
      { label: "4. Simulate\ninteraction", desc: "Choose process via\nMC sampling", color: "#EF4444", bg: "#FEE2E2" },
      { label: "5. Generate\nsecondaries", desc: "Create new particles,\nupdate kinematics", color: "#F59E0B", bg: "#FEF3C7" },
    ];
    steps.forEach((s, i) => {
      const x = 10 + i * 110, y = 65, w = 100, h = 95;
      ctx.fillStyle = s.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.fill();
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.stroke();
      ctx.fillStyle = s.color; ctx.font = "bold 10px Inter,sans-serif";
      s.label.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 18 + li * 13));
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      s.desc.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 52 + li * 13));
      if (i < 4) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x + w + 2, y + h / 2); ctx.lineTo(x + w + 8, y + h / 2); ctx.stroke();
      }
    });

    // Physics processes
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Physics Processes Simulated", W / 2, 185);
    const procs = [
      { cat: "EM", examples: "Ionization, bremsstrahlung, pair production, Compton, photoelectric", color: "#3B82F6" },
      { cat: "Hadronic", examples: "Elastic/inelastic nuclear, fission, capture, spallation", color: "#EF4444" },
      { cat: "Decay", examples: "Weak decays, de-excitation, radioactive decay chains", color: "#8B5CF6" },
      { cat: "Optical", examples: "Cherenkov, scintillation, absorption, Rayleigh scattering", color: "#10B981" },
    ];
    procs.forEach((p, i) => {
      const y = 200 + i * 22;
      ctx.fillStyle = p.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(p.cat, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(p.examples, 110, y);
    });
    ctx.textAlign = "center";

    // Step length diagram
    ctx.fillStyle = "#F8FAFC";
    ctx.beginPath(); ctx.roundRect(30, 295, 500, 55, 10); ctx.fill();
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(30, 295, 500, 55, 10); ctx.stroke();
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("Step Length Sampling", W / 2, 312);
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    ctx.fillText("s = −λ · ln(ξ)    where ξ ∈ (0,1) is uniform random, λ = mean free path", W / 2, 332);
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Repeat until particle stops, escapes, or energy < threshold", W / 2, 348);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.4 – GEANT4 Geometry and Physics Lists */
export const GEANT4Setup = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("GEANT4  -  Geometry & Physics Lists", W / 2, 24);

    // Three pillars of a GEANT4 simulation
    const pillars = [
      {
        title: "Geometry", color: "#3B82F6", bg: "#DBEAFE",
        items: [
          "World volume (mother)",
          "Logical volumes (shape+material)",
          "Physical volumes (placement)",
          "G4Box, G4Tubs, G4Sphere...",
          "Boolean solids (union/subtract)",
        ],
      },
      {
        title: "Physics List", color: "#8B5CF6", bg: "#EDE9FE",
        items: [
          "Which processes to simulate",
          "FTFP_BERT (general HEP)",
          "QGSP_BIC (low energy hadron)",
          "G4EmStandard (EM only)",
          "Optical photon physics",
        ],
      },
      {
        title: "Primary Generator", color: "#10B981", bg: "#D1FAE5",
        items: [
          "G4ParticleGun (single)",
          "G4GeneralParticleSource",
          "HepMC interface (Pythia)",
          "Define: particle, energy,",
          "position, direction",
        ],
      },
    ];
    pillars.forEach((p, i) => {
      const x = 15 + i * 185, y = 45, w = 172, h = 155;
      ctx.fillStyle = p.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
      ctx.strokeStyle = p.color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
      ctx.fillStyle = p.color; ctx.font = "bold 13px Inter,sans-serif";
      ctx.fillText(p.title, x + w / 2, y + 22);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      p.items.forEach((it, ii) => ctx.fillText(it, x + w / 2, y + 42 + ii * 17));
    });

    // User actions
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("User Action Classes", W / 2, 220);
    const actions = [
      { cls: "RunAction", desc: "Called at start/end of each run (booking histograms)", color: "#3B82F6" },
      { cls: "EventAction", desc: "Called per event (accumulate hits)", color: "#8B5CF6" },
      { cls: "SteppingAction", desc: "Called every step (record energy deposit, position)", color: "#10B981" },
      { cls: "TrackingAction", desc: "Called when track created/destroyed", color: "#F59E0B" },
      { cls: "SensitiveDetector", desc: "Collect hits in specific volumes (your detector!)", color: "#EF4444" },
    ];
    actions.forEach((a, i) => {
      const y = 235 + i * 22;
      ctx.fillStyle = a.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(a.cls, 40, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(a.desc, 190, y);
    });
    ctx.textAlign = "center";
    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Written in C++  -  used by CERN, NASA, medical physics", W / 2, 370);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.5 – Validating BL4S Detector Design with Simulation */
export const BL4SDetectorValidation = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Validating BL4S Detector Design with GEANT4", W / 2, 24);

    // Workflow arrows
    const workflow = [
      { label: "Design\ndetector", color: "#3B82F6", bg: "#DBEAFE" },
      { label: "Build GEANT4\nmodel", color: "#8B5CF6", bg: "#EDE9FE" },
      { label: "Run\nsimulation", color: "#10B981", bg: "#D1FAE5" },
      { label: "Compare to\nexpectations", color: "#F59E0B", bg: "#FEF3C7" },
      { label: "Iterate &\noptimise", color: "#EF4444", bg: "#FEE2E2" },
    ];
    workflow.forEach((w, i) => {
      const x = 10 + i * 110, y = 45, bw = 100, h = 55;
      ctx.fillStyle = w.bg;
      ctx.beginPath(); ctx.roundRect(x, y, bw, h, 8); ctx.fill();
      ctx.strokeStyle = w.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, bw, h, 8); ctx.stroke();
      ctx.fillStyle = w.color; ctx.font = "bold 10px Inter,sans-serif";
      w.label.split("\n").forEach((ln, li) => ctx.fillText(ln, x + bw / 2, y + 22 + li * 13));
      if (i < 4) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x + bw + 2, y + h / 2); ctx.lineTo(x + bw + 8, y + h / 2); ctx.stroke();
      }
    });

    // What to validate
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Key Validation Checks", W / 2, 125);
    const checks = [
      { check: "TOF resolution", question: "Can we separate π/K/p at our beam momentum?", status: "Must achieve σ_t < 200 ps", color: "#3B82F6" },
      { check: "Scintillator response", question: "Light yield sufficient for single-particle detection?", status: "Check # photoelectrons", color: "#8B5CF6" },
      { check: "Cherenkov threshold", question: "Which particles produce Cherenkov light in our gas?", status: "Verify n and β_th", color: "#10B981" },
      { check: "Trigger efficiency", question: "Does our coincidence logic capture events?", status: "Sim trigger + deadtime", color: "#F59E0B" },
      { check: "Background rejection", question: "Can we distinguish signal from noise/pileup?", status: "S/B ratio from MC", color: "#EF4444" },
    ];
    checks.forEach((ch, i) => {
      const y = 140 + i * 38;
      ctx.fillStyle = ch.color + "12";
      ctx.beginPath(); ctx.roundRect(15, y, 530, 32, 6); ctx.fill();
      ctx.strokeStyle = ch.color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.roundRect(15, y, 530, 32, 6); ctx.stroke();
      ctx.fillStyle = ch.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(ch.check, 25, y + 13);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(ch.question, 160, y + 13);
      ctx.fillStyle = "#64748B"; ctx.font = "10px Inter,sans-serif";
      ctx.fillText(ch.status, 160, y + 27);
    });
    ctx.textAlign = "center";

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("Simulation is mandatory in every BL4S proposal  -  reviewers expect GEANT4 results", W / 2, 345);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.6 – Event Generation and the Simulation Chain */
export const SimulationChain = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 360;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Event Generation & Simulation Chain", W / 2, 24);

    // Full chain: Gen → Sim → Digi → Reco → Analysis
    const chain = [
      { name: "Event\nGeneration", tool: "Pythia, Herwig\nSherpa", desc: "Hard scatter + parton shower + hadronisation", color: "#3B82F6", bg: "#DBEAFE" },
      { name: "Detector\nSimulation", tool: "GEANT4\nFLUKA", desc: "Transport particles through detector geometry", color: "#8B5CF6", bg: "#EDE9FE" },
      { name: "Digitisation", tool: "Experiment-\nspecific", desc: "Convert energy deposits to electronics signals", color: "#10B981", bg: "#D1FAE5" },
      { name: "Reconstruction", tool: "ROOT, ML\nalgorithms", desc: "Pattern recognition: hits → tracks → particles", color: "#F59E0B", bg: "#FEF3C7" },
      { name: "Analysis", tool: "ROOT, Python\nRDataFrame", desc: "Apply cuts, fit distributions, extract physics", color: "#EF4444", bg: "#FEE2E2" },
    ];
    chain.forEach((c2, i) => {
      const x = 10 + i * 110, y = 45, w = 102, h = 140;
      ctx.fillStyle = c2.bg;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.fill();
      ctx.strokeStyle = c2.color; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.stroke();
      ctx.fillStyle = c2.color; ctx.font = "bold 10px Inter,sans-serif";
      c2.name.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 18 + li * 12));
      ctx.fillStyle = "#64748B"; ctx.font = "9px Inter,sans-serif";
      c2.tool.split("\n").forEach((ln, li) => ctx.fillText(ln, x + w / 2, y + 52 + li * 12));
      ctx.fillStyle = "#475569"; ctx.font = "10px Inter,sans-serif";
      // Word wrap desc
      const words = c2.desc.split(" ");
      let line = "", ly = y + 85;
      words.forEach((word) => {
        const test = line + word + " ";
        if (ctx.measureText(test).width > w - 10 && line.length > 0) {
          ctx.fillText(line.trim(), x + w / 2, ly); ly += 12; line = word + " ";
        } else { line = test; }
      });
      if (line.trim()) ctx.fillText(line.trim(), x + w / 2, ly);
      if (i < 4) {
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(x + w + 2, y + h / 2); ctx.lineTo(x + w + 6, y + h / 2); ctx.stroke();
      }
    });

    // Data flow sizes
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Data Volume at Each Stage (LHC scale)", W / 2, 210);
    const stages = [
      { stage: "Generated truth", size: "~10 KB/event", color: "#3B82F6" },
      { stage: "Simulated hits", size: "~1 MB/event", color: "#8B5CF6" },
      { stage: "Digitised signals", size: "~1.5 MB/event", color: "#10B981" },
      { stage: "Reconstructed", size: "~0.5 MB/event", color: "#F59E0B" },
      { stage: "Analysis (AOD)", size: "~50 KB/event", color: "#EF4444" },
    ];
    stages.forEach((s, i) => {
      const y = 225 + i * 18;
      ctx.fillStyle = s.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(s.stage, 80, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(s.size, 280, y);
    });
    ctx.textAlign = "center";

    // BL4S scale
    ctx.fillStyle = "#D1FAE5";
    ctx.beginPath(); ctx.roundRect(30, 325, 500, 30, 8); ctx.fill();
    ctx.strokeStyle = "#10B981"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.roundRect(30, 325, 500, 30, 8); ctx.stroke();
    ctx.fillStyle = "#065F46"; ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("BL4S: simpler chain  -  GEANT4 + ROOT analysis on a laptop is feasible!", W / 2, 344);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};

/** 25.10 – Detector Response Simulation and Analysis Workflows */
export const DetectorResponseSim = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560, H = 380;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Detector Response Simulation & Analysis", W / 2, 24);

    // Truth vs Reconstructed
    ctx.fillStyle = "#DBEAFE";
    ctx.beginPath(); ctx.roundRect(20, 45, 245, 100, 10); ctx.fill();
    ctx.strokeStyle = "#3B82F6"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(20, 45, 245, 100, 10); ctx.stroke();
    ctx.fillStyle = "#1E40AF"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Truth (MC truth)", 142, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    const truthItems = ["Exact particle type, energy, direction", "Perfect vertex positions", "Known decay chains"];
    truthItems.forEach((t, i) => ctx.fillText(t, 142, 85 + i * 15));

    ctx.fillStyle = "#FEE2E2";
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 100, 10); ctx.fill();
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.roundRect(290, 45, 250, 100, 10); ctx.stroke();
    ctx.fillStyle = "#991B1B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Reconstructed (reco)", 415, 65);
    ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
    const recoItems = ["Measured with resolution smearing", "Inefficiencies, dead channels", "Fake tracks, misidentification"];
    recoItems.forEach((r, i) => ctx.fillText(r, 415, 85 + i * 15));

    // Arrow between
    ctx.strokeStyle = "#F59E0B"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(265, 95); ctx.lineTo(288, 95); ctx.stroke();
    ctx.fillStyle = "#F59E0B"; ctx.font = "bold 11px Inter,sans-serif";
    "Detector\nresponse".split("\n").forEach((ln, i) => ctx.fillText(ln, 277, 80 + i * 14));

    // Response modelling
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Detector Response Modelling", W / 2, 170);
    const resp = [
      { effect: "Energy smearing", model: "σ(E)/E = a/√E ⊕ b/E ⊕ c (calorimeter)", color: "#3B82F6" },
      { effect: "Position resolution", model: "σ_x = pitch/√12 (silicon strip), ~100 μm (drift)", color: "#8B5CF6" },
      { effect: "Timing resolution", model: "σ_t ~ 50–200 ps (scintillator + SiPM)", color: "#10B981" },
      { effect: "Efficiency", model: "ε(p_T, η) from data-driven tag-and-probe", color: "#F59E0B" },
      { effect: "Pile-up", model: "Add <μ> ~ 40 extra pp interactions (LHC)", color: "#EF4444" },
    ];
    resp.forEach((r, i) => {
      const y = 185 + i * 22;
      ctx.fillStyle = r.color; ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left"; ctx.fillText(r.effect, 30, y);
      ctx.fillStyle = "#475569"; ctx.font = "11px Inter,sans-serif";
      ctx.fillText(r.model, 165, y);
    });
    ctx.textAlign = "center";

    // Analysis workflow
    ctx.fillStyle = "#1E293B"; ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("Typical Analysis Workflow", W / 2, 310);
    const flow = [
      "MC Truth → Apply detector response → Reconstruct → Apply selection cuts",
      "→ Compare MC prediction with real data → Extract physics parameters",
    ];
    ctx.fillStyle = "#475569"; ctx.font = "12px Inter,sans-serif";
    flow.forEach((f, i) => ctx.fillText(f, W / 2, 330 + i * 18));

    ctx.fillStyle = "#64748B"; ctx.font = "11px Inter,sans-serif";
    ctx.fillText("If MC ≈ data in control regions → trust MC for signal extraction", W / 2, 370);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas ref={c} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: "100%" }} />
    </div>
  );
};
